import React, { useState } from 'react';
import { XMarkIcon, TagIcon } from '@heroicons/react/24/outline';
import { BandChickDto, ChickInfo } from '../../api/chicks';
import { Gender, GenderDeterminationMethod } from '../../utils/enums';
import BirdGenotypeEditor from '../genetics/BirdGenotypeEditor';
import EggHatchPredictionPanel from '../genetics/EggHatchPredictionPanel';
import type { PendingGenotype } from '../../utils/genotypeEditorUtils';
import CageSelect from '../CageSelect';

interface BandChickFormProps {
  chick: ChickInfo;
  onSubmit: (data: BandChickDto) => void;
  onCancel: () => void;
}

export default function BandChickForm({ chick, onSubmit, onCancel }: BandChickFormProps) {
  const [formData, setFormData] = useState<BandChickDto>({
    bandId: '',
    bandingDate: new Date().toISOString().split('T')[0],
    gender: Gender.UNKNOWN,
    genderDeterminationMethod: GenderDeterminationMethod.VISUALLY_SEXED,
    cageNumber: chick.cageNumber, // Use the chick's current cage number
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [pendingGenotypes, setPendingGenotypes] = useState<PendingGenotype[]>([]);
  const [geneticsSummary, setGeneticsSummary] = useState({
    mutationName: '',
    phenotype: '',
    genotype: '',
  });

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.bandId.trim()) {
      newErrors.bandId = 'Band ID is required';
    }

    if (!formData.bandingDate) {
      newErrors.bandingDate = 'Banding date is required';
    }

    if (!formData.cageNumber.trim()) {
      newErrors.cageNumber = 'Cage number is required';
    }



    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare clean data that matches the backend DTO exactly
      const cleanData: BandChickDto = {
        bandId: formData.bandId.trim(),
        gender: formData.gender,
        genderDeterminationMethod: formData.genderDeterminationMethod,
        bandingDate: formData.bandingDate,
        cageNumber: formData.cageNumber.trim(),
      };

      // Add optional fields only if they have values
      if (formData.notes?.trim()) {
        cleanData.notes = formData.notes.trim();
      }

      if (pendingGenotypes.length) {
        cleanData.genotypes = pendingGenotypes;
        if (geneticsSummary.mutationName) cleanData.mutationName = geneticsSummary.mutationName;
        if (geneticsSummary.phenotype) cleanData.phenotype = geneticsSummary.phenotype;
        if (geneticsSummary.genotype) cleanData.genotype = geneticsSummary.genotype;
      }

      await onSubmit(cleanData);
    } catch (error) {
      console.error('Error banding chick:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BandChickDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <TagIcon className="h-6 w-6 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-slate-900">Band Chick</h3>
        </div>
        <button
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Chick Info Summary */}
      <div className="bg-cyan-50 rounded-lg p-4 mb-6 border border-blue-200">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Chick Information</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-cyan-700 font-medium">Species:</span>
            <span className="ml-2 text-blue-900">{chick.species?.commonName || 'Unknown'}</span>
          </div>
          <div>
            <span className="text-cyan-700 font-medium">Age:</span>
            <span className="ml-2 text-blue-900">{chick.ageInDays} days old</span>
          </div>
          <div>
            <span className="text-cyan-700 font-medium">Hatch Date:</span>
            <span className="ml-2 text-blue-900">{chick.hatchDate.toLocaleDateString()}</span>
          </div>
          <div>
            <span className="text-cyan-700 font-medium">Cage:</span>
            <span className="ml-2 text-blue-900">{chick.cageNumber}</span>
          </div>
        </div>
      </div>

      {chick.eggId && (
        <div className="mb-6">
          <EggHatchPredictionPanel eggId={chick.eggId} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="bandId" className="block text-sm font-medium text-slate-700">
            Band ID *
          </label>
          <input
            type="text"
            id="bandId"
            value={formData.bandId}
            onChange={(e) => handleInputChange('bandId', e.target.value)}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
              errors.bandId ? 'border-red-300' : ''
            }`}
            placeholder="Enter unique band ID"
          />
          {errors.bandId && <p className="mt-1 text-sm text-red-600">{errors.bandId}</p>}
          <p className="mt-1 text-xs text-slate-500">
            This will become the bird's permanent identification
          </p>
        </div>

        <div>
          <label htmlFor="bandingDate" className="block text-sm font-medium text-slate-700">
            Banding Date *
          </label>
          <input
            type="date"
            id="bandingDate"
            value={formData.bandingDate}
            onChange={(e) => handleInputChange('bandingDate', e.target.value)}
            className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm ${
              errors.bandingDate ? 'border-red-300' : ''
            }`}
          />
          {errors.bandingDate && <p className="mt-1 text-sm text-red-600">{errors.bandingDate}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-slate-700">
              Gender
            </label>
            <select
              id="gender"
              value={formData.gender}
              onChange={(e) => handleInputChange('gender', e.target.value as Gender)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            >
              {Object.values(Gender).map(gender => (
                <option key={gender} value={gender}>
                  {gender.charAt(0) + gender.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="genderMethod" className="block text-sm font-medium text-slate-700">
              Gender Determination Method
            </label>
            <select
              id="genderMethod"
              value={formData.genderDeterminationMethod}
              onChange={(e) => handleInputChange('genderDeterminationMethod', e.target.value as GenderDeterminationMethod)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            >
              {Object.values(GenderDeterminationMethod).map(method => (
                <option key={method} value={method}>
                  {method.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
            Cage Number *
          </label>
          <CageSelect
            id="cageNumber"
            value={formData.cageNumber}
            onChange={(cageNumber) => handleInputChange('cageNumber', cageNumber)}
            required
            error={!!errors.cageNumber}
            placeholder="Search or add a cage number…"
          />
          {errors.cageNumber && <p className="mt-1 text-sm text-red-600">{errors.cageNumber}</p>}
          <p className="mt-1 text-xs text-slate-500">
            Current cage: {chick.cageNumber} (can be changed)
          </p>
        </div>



        {chick.species?.id ? (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/30 p-4">
            <h4 className="text-sm font-medium text-indigo-900 mb-3">Genetics (optional)</h4>
            <BirdGenotypeEditor
              speciesId={chick.species.id}
              gender={formData.gender}
              value={pendingGenotypes}
              autoSave={false}
              compact
              onChange={(genotypes, summary) => {
                setPendingGenotypes(genotypes);
                setGeneticsSummary(summary);
              }}
            />
          </div>
        ) : null}

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
            Banding Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            value={formData.notes || ''}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            placeholder="Enter any observations or notes about the banding process..."
          />
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <TagIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Important Note
              </h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  Banding this chick will:
                </p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>Create a permanent bird record in the system</li>
                  <li>Mark the chick as officially banded</li>
                  <li>Assign the specified band ID as the bird's permanent identifier</li>
                  <li>Transfer management from the chicks to the birds module</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50/80"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? (
              'Banding...'
            ) : (
              <>
                <TagIcon className="h-4 w-4 mr-2" />
                Band Chick
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
} 